import { Component, DestroyRef, inject, Inject, OnInit, Renderer2, ViewContainerRef } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { LocalStorageKeys } from './shared/types/local-storage-keys.enum';

import { RouterModule } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { KorviuModalService } from './core/services/korviu-modal.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { en_US, es_ES, NzI18nService } from 'ng-zorro-antd/i18n';
import { enUS, es } from 'date-fns/locale';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
  imports: [RouterModule],
  // NzModalModule can provide another service in a standalone injector; share the root modal context.
  providers: [{ provide: NzModalService, useExisting: KorviuModalService }]
})
export class AppComponent implements OnInit {
  private readonly modalService = inject(KorviuModalService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dateI18n = inject(NzI18nService);

  public title: string = 'unigana';

  constructor(
    private _translateService: TranslateService,
    private _renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this.modalService.setDefaultViewContainerRef(this.viewContainerRef, this.destroyRef);
  }

  public ngOnInit(): void {
    this._translateService.setDefaultLang('es');
    this._setOnLangChangeListener();
    this._setUserLanguage();
  }

  private _setUserLanguage(): void {
    const languageFromLocalStorage: string = localStorage.getItem(LocalStorageKeys.PREFERRED_LANGUAGE);
    const availableLanguages: string[] = ['en', 'es'];
    let language: string;
    if (languageFromLocalStorage && availableLanguages.includes(languageFromLocalStorage)) {
      language = languageFromLocalStorage;
    } else {
      language = 'es';
    }
    this._translateService.use(language);
    this._savePreferredLanguage(language);
    this._setHTMLLang(language);
    this._setDateLocale(language);
  }

  private _setOnLangChangeListener(): void {
    this._translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((langChangeEvent: LangChangeEvent) => {
        this._savePreferredLanguage(langChangeEvent?.lang);
        this._setHTMLLang(langChangeEvent?.lang);
        this._setDateLocale(langChangeEvent?.lang);
      });
  }

  private _setDateLocale(language: string): void {
    const spanish = language?.toLowerCase().startsWith('es');
    this.dateI18n.setDateLocale(spanish ? es : enUS);
    this.dateI18n.setLocale(spanish ? es_ES : en_US);
  }

  private _savePreferredLanguage(language: string): void {
    localStorage.setItem(LocalStorageKeys.PREFERRED_LANGUAGE, language);
  }

  private _setHTMLLang(lang: string) {
    this._renderer.setAttribute(this._document.documentElement, 'lang', lang);
  }
}
